/**
 * In-view fade primitives — port of the template's FadeImage behavior
 * (IntersectionObserver reveal, optional delay) for any element.
 * Respects prefers-reduced-motion: content renders visible immediately,
 * with no translate or transition (AI-11994).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// eslint-disable-next-line react-refresh/only-export-components -- hook + component share the fade contract
export function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [reduced] = useState(prefersReducedMotion);
  const [visible, setVisible] = useState(reduced);

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, reduced]);

  return { ref, visible, reduced };
}

export function FadeIn({
  children,
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  const { ref, visible, reduced } = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={
        reduced
          ? undefined
          : {
              opacity: visible ? 1 : 0,
              transform: visible ? "translateY(0)" : "translateY(24px)",
              transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
            }
      }
    >
      {children}
    </div>
  );
}
